/**
 * PublicSummaryDocument — condensed, plain-language version of a legal doc.
 * Shown at /privacy, /terms, etc. Rendered ONLY on public routes; the full
 * binding text lives at /dashboard/legal/:slug (members-only).
 */
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Shield, Printer, Lock, CheckCircle2, ArrowRight, FileText } from 'lucide-react';
import { EFFECTIVE_DATE, COMPANY } from '@/legal/legalContent';
import { useAuth } from '@/context/AuthContext';

export default function PublicSummaryDocument({ slug, summary, docTitle }) {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      {/* Back + print */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to="/legal" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-600" data-testid="pub-back-link">
          <ChevronLeft className="w-4 h-4" /> All legal documents
        </Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600" data-testid="pub-print-btn">
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-2">
          <Shield className="w-3 h-3" /> {COMPANY.name} · Public summary
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid={`pub-title-${slug}`}>{summary.title}</h1>
        <p className="text-sm text-slate-500 mt-2">{summary.tagline}</p>
        <div className="text-xs text-slate-400 mt-1">Effective {EFFECTIVE_DATE}</div>
      </div>

      {/* Summary bullets */}
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 p-6 mb-6">
        <ul className="space-y-3">
          {summary.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-800 dark:text-slate-200 leading-relaxed">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {summary.fullDocument && (
        <a href={summary.fullDocument.href} target="_blank" rel="noopener noreferrer"
           className="mb-6 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-sm font-semibold text-emerald-700 hover:border-emerald-400"
           data-testid="pub-full-document">
          <FileText className="w-4 h-4 shrink-0" /> {summary.fullDocument.label}
        </a>
      )}

      {/* Full-terms CTA */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="pub-full-terms-cta">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">Want the full binding {docTitle} document?</div>
            <p className="text-sm text-slate-500 mt-1">
              The complete, legally-binding version — with every clause, definition, and jurisdiction detail —
              is available inside your {COMPANY.name} account. Only members can view the full agreement they’re party to.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {user ? (
                <button
                  onClick={() => nav(summary.memberLink)}
                  data-testid="pub-goto-member-full"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2"
                >
                  Open the full {docTitle} in my account
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <>
                  <Link to={`/login?next=${encodeURIComponent(summary.memberLink)}`} data-testid="pub-signin-for-full">
                    <button className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2">
                      Sign in to read the full document
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </Link>
                  <Link to="/register" className="text-sm text-slate-500 hover:text-emerald-600 px-2 py-2">
                    Or create an account →
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer notice */}
      <div className="mt-8 text-xs text-slate-500 text-center">
        This public summary is for convenience only. In case of any conflict between this summary and the full member document,
        <br className="hidden sm:block" />
        the full member document controls. Legal questions: <a href={`mailto:${COMPANY.legalEmail}`} className="text-emerald-600 hover:underline">{COMPANY.legalEmail}</a>
      </div>
    </div>
  );
}
